import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
  RowSelectionState,
  OnChangeFn
} from "@tanstack/react-table";

import { Table } from "shared/app/Table";
import { useProgressContext } from "providers/ProgressContext";
import { useMemo, useState } from "react";
import { useCurrentRefinements } from "react-instantsearch-core";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { cx } from "shared/utils";
import { SubscribeModal } from "views/renters/components/Header/SubscribeModal";
import { RowDrawer } from "views/renters/components/Table/RowDrawer/RowDrawer";
import {
  columnHelperRenters,
  columnsForRentersTable
} from "views/renters/components/Table/constants";
import { AlgoliaUser } from "views/renters/types";
import { SubscriptionBadge, SubscriptionBadgeProps } from "./SubscriptionBadge";
import moment from "moment";

type Props = {
  results: any;
  setSelectedRentersRow: OnChangeFn<RowSelectionState>;
  selectedRentersRow: RowSelectionState;
};

export const RentersTable = ({
  results,
  setSelectedRentersRow,
  selectedRentersRow
}: Props) => {
  const [openModal, setOpenModal] = useState(false);
  const [showRowDrawer, setShowRowDrawer] = useState(false);
  // state for checkboxes
  const [selectedRowData, setSelectedRowData] = useState<AlgoliaUser | null>(
    null
  );
  //state for subscribe button
  const [subscribeData, setSubscribeData] = useState<AlgoliaUser | null>(null);

  const { syncSubscriptions, subscriptionsQueue } = useProgressContext();

  const { items } = useCurrentRefinements({
    includedAttributes: ["boomEnrollmentStatus"]
  });

  const currentTab = items[0]?.refinements[0]?.label || "all";

  const columns = useMemo(() => {
    return [
      ...columnsForRentersTable,
      columnHelperRenters.accessor((row) => row, {
        id: "subscribe_button fixed_width",
        header: "",
        size: 95,
        cell: (row) => {
          const value = row.getValue();

          return (
            <div
              className={cx(
                "h-8 max-w-[95px]",
                value.boomEnrollmentStatus === "enrolled" ? "hidden" : ""
              )}
              onClick={(e) => {
                e.stopPropagation();
                setSubscribeData(value);
                setOpenModal(true);
              }}
            >
              <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.XS}
                disabled={subscriptionsQueue.isSyncing}
              >
                Subscribe
              </Button>
            </div>
          );
        }
      })
    ];
  }, [subscriptionsQueue]);

  const table = useReactTable({
    data: results?.hits || [],
    columns: columns as ColumnDef<any, any>[],
    enableRowSelection: true,
    state: {
      rowSelection: selectedRentersRow,
      columnVisibility: {
        id: currentTab === "error" || currentTab === "not_enrolled"
      }
    },
    enableHiding: true,
    onRowSelectionChange: setSelectedRentersRow,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id
  });

  const openDrawer = (_, row: any) => {
    setShowRowDrawer(true);
    setSelectedRowData(row.original);
  };

  const closeDrawer = () => {
    setShowRowDrawer(false);
    setSelectedRowData(null);
  };

  return (
    <>
      <Table
        table={table}
        rowHeight="64px"
        onRowClick={openDrawer}
        mobileRowClassName="max-w-[calc(100vw-1.5rem)]"
        mobileRow={(row) => {
          return (
            <div>
              <p className="text-sm font-medium text-content-1000">
                {row.original.customer.firstName}{" "}
                {row.original.customer.lastName}
              </p>
              <p className="text-xs text-content-400 my-1">
                {row.original.property.name}

                <p className="text-xs text-content-400">
                  {row.original.building && row.original.unit
                    ? `${row.original.building}, ${row.original.unit}`
                    : row.original.building || row.original.unit}
                </p>
              </p>

              <div className="flex gap-2 items-center my-1">
                <SubscriptionBadge
                  status={
                    row.original
                      .boomEnrollmentStatus as SubscriptionBadgeProps["status"]
                  }
                />

                <p className="text-xs text-content-400">${row.original.rent}</p>
              </div>

              <div className="flex gap-2 items-center text-xs text-content-600">
                <p>
                  {row.original.startDate &&
                  moment(row.original.startDate).isValid()
                    ? moment(row.original.startDate).format("MMM DD, YYYY")
                    : "--"}
                </p>
                -
                <p>
                  {row.original.endDate &&
                  moment(row.original.endDate).isValid()
                    ? moment(row.original.endDate).format("MMM DD, YYYY")
                    : "--"}
                </p>
              </div>
            </div>
          );
        }}
      />
      <RowDrawer
        onClose={closeDrawer}
        showRowDrawer={showRowDrawer}
        selectedRowData={selectedRowData}
      />
      <SubscribeModal
        isOpen={openModal}
        setIsOpen={setOpenModal}
        renterName={`${subscribeData?.customer.firstName} ${subscribeData?.customer.lastName}`}
        enrollRenters={(enrollmentOption) => {
          syncSubscriptions({ renters: [subscribeData], enrollmentOption });
          //clear modal state
          setSubscribeData(null);
        }}
      />
    </>
  );
};
